import { toast } from 'sonner'

import { toStoreRequest } from '@/components/EntityCreationModals/StoreCreationModal/StoreCreationModal.config'
import {
  buildStoreSupplierUpdatePayload,
  resolveStoreStatusId,
} from '@/components/EntityCreationModals/StoreCreationModal/storeFormHelpers'
import { NULL_UUID_ID } from '@/constants'
import { stockImportsApi } from '@/pages/Exchanges/tabs/StockImports/api/stockImportsApi'
import type { StoreModel } from '@/pages/Exchanges/tabs/StockImports/types/stockImports.types'

import type { PendingStore } from './SupplierCreationModal.config'

type StoreBindingChanges = {
  attachIds: string[]
  detachIds: string[]
  newStores: PendingStore[]
}

type StoreBindingTask = {
  label: string
  run: () => Promise<unknown>
}

export type StoreBindingsResult = {
  failures: string[]
  changed: boolean
}

const requireStoreStatusId = async (
  store: StoreModel | null,
  stores: StoreModel[]
) => {
  const storeStatusId = await resolveStoreStatusId(store, stores)
  if (!storeStatusId) throw new Error('Не найден UUID статуса склада')
  return storeStatusId
}

export const applySupplierStoreBindings = async (
  supplierId: string,
  changes: StoreBindingChanges,
  stores: StoreModel[]
): Promise<StoreBindingsResult> => {
  const tasks: StoreBindingTask[] = changes.newStores.map((pending) => ({
    label: pending.values.description,
    run: async () =>
      stockImportsApi.createStore(
        toStoreRequest(
          {
            ...pending.values,
            supplier: { value: supplierId, label: '' },
          },
          await requireStoreStatusId(null, stores)
        )
      ),
  }))

  const bindings = [
    ...changes.attachIds.map((storeId) => ({ storeId, target: supplierId })),
    ...changes.detachIds.map((storeId) => ({ storeId, target: NULL_UUID_ID })),
  ]

  bindings.forEach(({ storeId, target }) => {
    const store = stores.find((item) => item.id === storeId)
    if (!store) return
    tasks.push({
      label: store.description,
      run: async () =>
        stockImportsApi.updateStore(
          store.id,
          buildStoreSupplierUpdatePayload(
            store,
            await requireStoreStatusId(store, stores),
            target
          )
        ),
    })
  })

  const failures: string[] = []
  let changed = false
  for (const task of tasks) {
    try {
      await task.run()
      changed = true
    } catch {
      failures.push(task.label)
    }
  }

  return { failures, changed }
}

export const notifyStoreBindingFailures = (
  failures: string[],
  isEdit: boolean
) => {
  if (failures.length === 0) return
  toast.error(
    isEdit
      ? 'Поставщик сохранён, но привязка складов выполнена не полностью'
      : 'Поставщик создан, но привязка складов выполнена не полностью',
    {
      description: `Не удалось обработать: ${failures.join(', ')}. Откройте поставщика и повторите.`,
    }
  )
}
